import React from "react";
import { ArrowLeft, CalendarHeart } from "lucide-react";
import { Alert, Button, Card, ICON, Spinner } from "./ui";

/**
 * Estados del ingreso:
 * - checking: Firebase todavía no dijo si hay sesión (o falta saber el rol).
 * - signed-out: no hay sesión.
 * - signing-in: la ventana de Google está abierta.
 * - unauthorized: hay sesión de Google, pero esa cuenta no tiene rol (§4.6).
 * - error: no se pudo abrir Google (ventana bloqueada, dominio sin autorizar…).
 */
export type LoginStatus = "checking" | "signed-out" | "signing-in" | "unauthorized" | "error";

type LoginProps = {
  status: LoginStatus;
  errorMessage?: string | null;
  onSignIn: () => void;
};

const GOOGLE_BUTTON =
  "flex h-12 w-full items-center justify-center gap-2.5 rounded-xl border border-adm-border-2 bg-adm-surface text-[15px] font-medium transition-colors cursor-pointer hover:bg-adm-card disabled:cursor-default disabled:hover:bg-adm-surface";

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

export function Login({ status, errorMessage, onSignIn }: LoginProps) {
  const busy = status === "checking" || status === "signing-in";
  return (
    <div className="grid min-h-screen place-items-center p-6">
      <Card className="flex w-full max-w-[400px] flex-col items-center gap-5 px-9 py-11 text-center shadow-adm max-md:px-[22px] max-md:py-9">
        <div className="grid size-12 place-items-center rounded-[14px] bg-adm-acento-suave text-adm-acento-texto">
          <CalendarHeart size={24} strokeWidth={1.75} />
        </div>
        <div className="flex flex-col items-center gap-1.5">
          <h1 className="text-2xl font-semibold leading-[1.2] tracking-[-.02em]">Farmacias de turno Tandil</h1>
          <p className="max-w-[280px] text-[13px] leading-[1.55] text-adm-t55">
            Cargá los turnos del mes y armá los carteles para las vidrieras.
          </p>
        </div>

        {status === "unauthorized" && (
          <Alert tone="red" className="w-full text-left">
            Esa cuenta de Google no tiene permiso para el panel.
          </Alert>
        )}
        {status === "error" && errorMessage && (
          <Alert tone="red" className="w-full text-left">
            {errorMessage}
          </Alert>
        )}

        {busy ? (
          <button type="button" className={GOOGLE_BUTTON} disabled>
            <Spinner />
            {status === "signing-in" ? "Abriendo Google…" : "Revisando tu sesión…"}
          </button>
        ) : (
          <button type="button" className={GOOGLE_BUTTON} onClick={onSignIn}>
            <GoogleMark />
            Ingresar con Google
          </button>
        )}

        <Button variant="ghost" size="sm" href="/" icon={<ArrowLeft {...ICON} />}>
          Volver a la web
        </Button>
      </Card>
    </div>
  );
}
